import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { headCountFixupSliceInitialState } from './headCountFixupInitialState';
import {
    getUpdatedFixupRecordsToDelete,
    getUpdatedFixupRecordsToUpdate,
    getRecordRowsAndLotPensAfterFixupByDateFromInitialLoad,
    getHistoryFromHistoryResponse,
} from './helpers';
import { IHeadCountFixupErrorData } from '../hooks/interfaces';
import dayjs from '../../../../../../utils/dayjs';
import {
    IFixupRecordDeleteStatusPayload,
    IFixupRecordUpdateHeadCountPayload,
} from './interface';
import { IHistory } from '../interfaces';
import { internalDateFormat } from '../constants';

const headCountFixupSlice = createSlice({
    name: 'headCountFixup',
    initialState: headCountFixupSliceInitialState,
    reducers: {
        setShowHeadCountFixupRequiredModal: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.showHeadCountFixupRequiredModal = action.payload;
        },
        closeFixupPage: state => {
            Object.assign(state, headCountFixupSliceInitialState);
        },
        openFixupPageWithErrorData: (
            state,
            action: PayloadAction<Required<IHeadCountFixupErrorData>>,
        ) => {
            state.showHeadCountFixupPage = true;

            //make sure previous state data is overwritten/cleared
            state.fixup = action.payload.fixup;
            state.initialHistory = getHistoryFromHistoryResponse(
                action.payload.history,
                action.payload.fixup.originalRecord.recordId,
                action.payload.fixup.originalRecord.type,
            );

            const { lotPensAfterFixupByDate, recordRowsAfterFixupByDate } =
                getRecordRowsAndLotPensAfterFixupByDateFromInitialLoad(state);
            state.recordRowsAfterFixupByDate = recordRowsAfterFixupByDate;
            state.lotPensAfterFixupByDate = lotPensAfterFixupByDate;
        },
        setFixupRecordUpdateHeadCount: (
            state,
            action: PayloadAction<IFixupRecordUpdateHeadCountPayload>,
        ) => {
            state.formChanged = true;

            const {
                recordId,
                recordType,
                penId,
                recordDate: unformattedDate,
                updatedHdCountChange,
            } = action.payload;
            const recordDate =
                dayjs(unformattedDate).format(internalDateFormat);

            const historyRecord = state.recordRowsAfterFixupByDate[
                recordDate
            ]?.find(
                record =>
                    record.recordId === recordId &&
                    record.recordType === recordType &&
                    record.penId === penId &&
                    !record.isInitiallyModifiedRecord,
            );

            if (historyRecord) {
                const hdCountDelta =
                    updatedHdCountChange - historyRecord.hdCountChange;
                //update record in history
                historyRecord.hdCountChange = updatedHdCountChange;

                const fromPenId = historyRecord.fromPenId;

                //update lotPens
                for (
                    let date = recordDate;
                    date <= state.initialHistory.startDate;
                    date = dayjs(date).add(1, 'day').format(internalDateFormat)
                ) {
                    const lotPensForDate = state.lotPensAfterFixupByDate[date];

                    const lotPenForDate = lotPensForDate?.find(
                        lotPen => lotPen.penId === penId,
                    );
                    if (lotPenForDate) {
                        lotPenForDate.hdCount += hdCountDelta;
                    }

                    //handle from pen
                    if (fromPenId) {
                        const fromLotPenForDate = lotPensForDate?.find(
                            lotPen => lotPen.penId === fromPenId,
                        );
                        if (fromLotPenForDate) {
                            fromLotPenForDate.hdCount -= hdCountDelta;
                        }
                    }
                }
            }

            //update fixup records to update
            state.fixup.recordsToUpdate = getUpdatedFixupRecordsToUpdate(
                state,
                action.payload,
            );
        },
        setFixupRecordDeleteStatus: (
            state,
            action: PayloadAction<IFixupRecordDeleteStatusPayload>,
        ) => {
            const {
                recordId,
                recordType,
                penId,
                recordDate: unformattedDate,
                isDeleted,
            } = action.payload;
            const recordDate =
                dayjs(unformattedDate).format(internalDateFormat);

            const historyRecords = state.recordRowsAfterFixupByDate[
                recordDate
            ]?.filter(
                record =>
                    record.recordId === recordId &&
                    record.recordType === recordType &&
                    (penId === -1 || record.penId === penId) && //penId of -1 means all pens for record
                    !record.isInitiallyModifiedRecord,
            );

            if (historyRecords && historyRecords.length > 0) {
                historyRecords.forEach(historyRecord => {
                    //update record in history
                    historyRecord.isDeleted = isDeleted;

                    const fromPenId = historyRecord.fromPenId;

                    for (
                        let date = recordDate;
                        date <= state.initialHistory.startDate;
                        date = dayjs(date)
                            .add(1, 'day')
                            .format(internalDateFormat)
                    ) {
                        //update lotPens
                        const lotPensForDate =
                            state.lotPensAfterFixupByDate[date];

                        const lotPenForDate = lotPensForDate?.find(
                            lotPen => lotPen.penId === action.payload.penId,
                        );
                        if (lotPenForDate) {
                            lotPenForDate.hdCount += isDeleted
                                ? -historyRecord.hdCountChange
                                : historyRecord.hdCountChange;
                        }

                        //handle from pen
                        if (fromPenId) {
                            const fromLotPenForDate = lotPensForDate?.find(
                                lotPen => lotPen.penId === fromPenId,
                            );
                            if (fromLotPenForDate) {
                                fromLotPenForDate.hdCount += isDeleted
                                    ? historyRecord.hdCountChange
                                    : -historyRecord.hdCountChange;
                            }
                        }
                    }
                });
            }

            //update fixup records to delete
            state.fixup.recordsToDelete = getUpdatedFixupRecordsToDelete(
                state,
                action.payload,
            );
            return;
        },
        getMoreAuditHistory: (state, action: PayloadAction<IHistory>) => {
            state.initialHistory.endDate = action.payload.endDate ?? '';
            const history = getHistoryFromHistoryResponse(action.payload);

            state.initialHistory.moveTypeRecordPenHeadCountChanges = [
                ...state.initialHistory.moveTypeRecordPenHeadCountChanges,
                ...history.moveTypeRecordPenHeadCountChanges,
            ];

            const { lotPensAfterFixupByDate, recordRowsAfterFixupByDate } =
                getRecordRowsAndLotPensAfterFixupByDateFromInitialLoad(state);
            state.recordRowsAfterFixupByDate = recordRowsAfterFixupByDate;
            state.lotPensAfterFixupByDate = lotPensAfterFixupByDate;
        },
        refreshMoveRecords: (state, action: PayloadAction<IHistory>) => {
            state.initialHistory.endDate = action.payload.endDate ?? '';
            const history = getHistoryFromHistoryResponse(action.payload);

            state.initialHistory.moveTypeRecordPenHeadCountChanges = [
                ...history.moveTypeRecordPenHeadCountChanges,
            ];

            const { lotPensAfterFixupByDate, recordRowsAfterFixupByDate } =
                getRecordRowsAndLotPensAfterFixupByDateFromInitialLoad(state);
            state.recordRowsAfterFixupByDate = recordRowsAfterFixupByDate;
            state.lotPensAfterFixupByDate = lotPensAfterFixupByDate;

            state.initialHistory.lastAuditCreatedDatetime =
                action.payload.lastAuditCreatedDatetime;
        },
    },
});

export const headCountFixupPageActions = headCountFixupSlice.actions;
export const headCountFixupPageReducer = headCountFixupSlice.reducer;
